import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ProductCard } from "../../components/site/Cards";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, BarChart, Bar } from "recharts";
import { Trophy, Target, CheckCircle2, BookOpen, ArrowRight, Play } from "lucide-react";

const StatCard = ({ icon: Icon, label, value, testid }) => (
  <div className="card-surface p-5" data-testid={testid}>
    <Icon className="w-5 h-5 text-ember mb-3" />
    <div className="font-heading font-bold text-2xl">{value}</div>
    <div className="text-xs text-content-muted uppercase tracking-wide mt-1">{label}</div>
  </div>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/student/dashboard").then((r) => setD(r.data)).catch(() => {}); }, []);
  if (!d) return <div className="py-20 text-center text-content-muted">Loading…</div>;
  const s = d.stats;
  return (
    <div className="space-y-8">
      <div>
        <h1 className="font-heading text-2xl font-bold">Hi, {user?.name?.split(" ")[0]} 👋</h1>
        <p className="text-content-secondary text-sm mt-1">Here's your preparation snapshot.</p>
      </div>

      {d.in_progress.length > 0 && (
        <div className="card-surface p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 border-ember/40">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-ember-soft flex items-center justify-center"><Play className="w-5 h-5 text-ember" /></div>
            <div><div className="text-sm font-medium">Resume your test</div><div className="text-xs text-content-muted">{d.in_progress[0].test_name}</div></div>
          </div>
          <Link to={`/test/${d.in_progress[0].test_id}/attempt`} className="btn-ember rounded-full px-5 py-2 text-sm" data-testid="dashboard-resume">Continue</Link>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard icon={BookOpen} label="Series owned" value={s.series_owned} testid="stat-series" />
        <StatCard icon={CheckCircle2} label="Tests done" value={s.tests_completed} testid="stat-tests" />
        <StatCard icon={Target} label="Avg accuracy" value={`${s.avg_accuracy}%`} testid="stat-accuracy" />
        <StatCard icon={Trophy} label="Avg score" value={`${s.avg_score}%`} testid="stat-score" />
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="card-surface p-5">
          <h3 className="font-heading font-semibold text-sm mb-4">Score trend</h3>
          {d.trend.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <LineChart data={d.trend}>
                <XAxis dataKey="name" stroke="#64748B" fontSize={10} tickLine={false} />
                <YAxis stroke="#64748B" fontSize={10} tickLine={false} />
                <Tooltip contentStyle={{ background: "#151F30", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                <Line type="monotone" dataKey="score" stroke="#F97316" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          ) : <Empty text="Attempt tests to see your trend." />}
        </div>
        <div className="card-surface p-5">
          <h3 className="font-heading font-semibold text-sm mb-4">Weak subjects</h3>
          {d.weak_subjects.length ? (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={d.weak_subjects}>
                <XAxis dataKey="subject" stroke="#64748B" fontSize={10} tickLine={false} />
                <YAxis stroke="#64748B" fontSize={10} tickLine={false} />
                <Tooltip contentStyle={{ background: "#151F30", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, fontSize: 12 }} />
                <Bar dataKey="accuracy" fill="#F97316" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          ) : <Empty text="No data yet." />}
        </div>
      </div>

      <div>
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-heading font-semibold">My test series</h3>
          <Link to="/student/test-series" className="text-sm text-ember flex items-center gap-1">View all <ArrowRight className="w-3.5 h-3.5" /></Link>
        </div>
        {d.products.length ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {d.products.slice(0, 3).map((p) => (
              <Link key={p.id} to={`/student/series/${p.id}`} className="card-surface p-5 hover:border-ember/60">
                <div className="text-xs text-ember uppercase tracking-wider">{p.type}</div>
                <div className="font-heading font-semibold text-sm mt-1.5">{p.name}</div>
                <div className="text-xs text-content-muted mt-2">{p.num_tests}+ tests</div>
              </Link>
            ))}
          </div>
        ) : (
          <div className="card-surface p-8 text-center">
            <p className="text-content-secondary text-sm mb-4">You haven't purchased any test series yet.</p>
            <Link to="/pricing" className="btn-ember rounded-full px-5 py-2 text-sm inline-block">Browse test series</Link>
          </div>
        )}
      </div>

      {d.recommended.length > 0 && (
        <div>
          <h3 className="font-heading font-semibold mb-4">Recommended for you</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {d.recommended.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        </div>
      )}
    </div>
  );
}
const Empty = ({ text }) => <div className="h-[200px] flex items-center justify-center text-sm text-content-muted">{text}</div>;
